import { Box } from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { IconSpy } from "@tabler/icons-react"
import { useQueryClient } from "@tanstack/react-query"
import type { PropsWithChildren } from "react"
import { SuperOnly } from "@/components"
import { useAuth, useUserOption } from "@/hooks"
import type { TAuth, TUser } from "@/types"
import { api } from "@/utils"

export function useImpersonate() {
  const queryClient = useQueryClient()
  const [value, setValue, reset] = useUserOption("impersonate", null)

  const getDisplayName = (user?: TUser) => user?.full_name || user?.name || "User"

  return {
    value,
    reset: () => {
      if (value?.token) {
        api.defaults.headers.Authorization = `Bearer ${value.token}`
        queryClient.resetQueries()
      }
      reset()
    },
    setImpersonate: async (values: any, auth) => {
      let name = getDisplayName(auth.user)
      const { id } = values
      setValue(values)

      if (id?.length > 0) {
        setValue({ ...values, token: auth.token })
        const { data } = await api.post<TAuth>(`/auth/impersonate`, { user: id })
        api.defaults.headers.Authorization = `Bearer ${data.token}`
        name = getDisplayName(data.user)
        queryClient.resetQueries()
      }

      notifications.show({
        title: "Impersonation",
        icon: <IconSpy />,
        color: "gray",
        message: `You are now impersonating ${name}.`,
      })

      return name
    },
  }
}

type TRecentImpersonatedUser = {
  id: string
  name?: string
}

export function Impersonate({ id, name, children }: PropsWithChildren<{ id: string; name?: string }>) {
  const auth = useAuth()
  const { setImpersonate } = useImpersonate()
  const [iids, setIids] = useUserOption("iids", [] as Array<string | TRecentImpersonatedUser>)
  const recentUsers = iids
    .map((item) => {
      if (!item) return null
      if (typeof item === "string") return { id: item, name: item }
      if (!item.id) return null
      return item
    })
    .filter((item): item is TRecentImpersonatedUser => !!item)

  return (
    <SuperOnly>
      <Box
        onClick={async () => {
          setIids([{ id, name: name || id }, ...recentUsers.filter((u) => u.id !== id)].slice(0, 10))
          const displayName = await setImpersonate({ id }, auth)
          setIids([{ id, name: displayName }, ...recentUsers.filter((u) => u.id !== id)].slice(0, 10))
        }}
      >
        {children}
      </Box>
    </SuperOnly>
  )
}
